import { useEffect, useState } from "react";

const STORAGE_KEY = "lulo_cookie_consent";
const OPEN_EVENT = "lulo:open-cookie-consent";

export const cookieConsent = {
  get: () => {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  },
  set: (v) => {
    try { localStorage.setItem(STORAGE_KEY, v); } catch { /* noop */ }
  },
  clear: () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
  },
  reopen: () => {
    cookieConsent.clear();
    window.dispatchEvent(new CustomEvent(OPEN_EVENT));
  },
};

function loadAnalytics() {
  if (typeof window !== "undefined" && typeof window.__loadPostHog === "function") {
    window.__loadPostHog();
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Decide initial visibility based on stored consent
    const v = cookieConsent.get();
    if (!v) setVisible(true);
    if (v === "all") loadAnalytics();
    // Trigger slide-up after first paint
    const t = window.setTimeout(() => setMounted(true), 30);

    const onOpen = () => {
      setVisible(true);
      setMounted(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, []);

  const acceptAll = () => {
    cookieConsent.set("all");
    loadAnalytics();
    setVisible(false);
  };

  const necessaryOnly = () => {
    cookieConsent.set("necessary");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:pb-6 pointer-events-none"
    >
      <div
        className={`pointer-events-auto mx-auto w-full max-w-[600px] rounded-xl bg-[#0A2540] text-white shadow-2xl ring-1 ring-white/10 transition-all duration-300 ease-out ${
          mounted ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
        }`}
      >
        <div className="p-4 sm:p-5">
          <p className="text-sm leading-6 text-slate-100">
            We use cookies to provide essential website functionality and
            optional analytics. You can accept all cookies or only the necessary
            ones. Learn more in our{" "}
            <a href="/privacy" className="underline decoration-white/40 underline-offset-4 hover:decoration-white">
              Privacy Policy
            </a>
            .
          </p>
          <div className="mt-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3">
            <button
              type="button"
              onClick={necessaryOnly}
              data-testid="cookie-necessary-btn"
              className="inline-flex items-center justify-center rounded-md border border-white/25 bg-transparent px-4 py-2 text-sm font-medium text-white hover:bg-white/10 transition-colors"
            >
              Necessary Only
            </button>
            <button
              type="button"
              onClick={acceptAll}
              data-testid="cookie-accept-btn"
              className="inline-flex items-center justify-center rounded-md bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1d4ed8] transition-colors"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
